import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Modal, TextArea } from "semantic-ui-react";
import { useToasts } from "react-toast-notifications";
import { Beforeunload } from "react-beforeunload";
import {
    selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserName,
    selectCurrentUserNotificationEmail, selectSseConnectionId
} from "../slices/currentUser/currentUserSlice";
import { changeConceptDelete, changeVotePermission, selectConceptCode, selectConceptName, selectConceptOpenForVoting, selectConceptSelected, selectVotePermission } from "../slices/codeSystemTermDetail/codeSystemTermDetailSlice";
import { selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";
import { editModeOn, selectEditMode } from "../slices/editMode/editModeSlice";
import { selectAllResources, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectChangedResourcesList } from "../slices/changedResourcesList/changedResourcesListSlice";
import { selectTabFocus } from "../slices/tabPane/tabPaneSlice";
import joinGroup from "../functions/joinGroupAPI";
import votePermissionCheck from "../functions/votePermissionCheckAPI";
import submitUpdatedResource from "./resourceDisplayCommonFunctions/submitUpdatedResource";
import submitCodeSystemComment from "./codeSystemResourceFunctions/submitCodeSystemComment";
import NavigationCodeSystemTermSegment from "./CodeSystemTermNavigation";

const TermCommentButton = ({ resourceId, codeSystemTitle,
    commentEntryState, setCommentEntryState }) => {
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const navigate = useNavigate();
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const conceptCode = useSelector(selectConceptCode);
    const conceptName = useSelector(selectConceptName);
    const conceptSelected = useSelector(selectConceptSelected);

    let disableButton = false;
    if (conceptCode) {
        disableButton = false;
    } else {
        disableButton = true;
    }
    let loggedInContent;
    let actions;
    let expandModalButtonBackgroundColor = "";
    let modalBodyContentStyle = { paddingLeft: "20px" };
    if (commentEntryState.modalExpanded === false) {
        expandModalButtonBackgroundColor = "#DAA520";
        modalBodyContentStyle["display"] = "none";
    }
    if (userId) {
        loggedInContent = <div style={modalBodyContentStyle}>
            {commentEntryState.commentSummary &&
                <Beforeunload onBeforeunload={(event) => { event.preventDefault(); }} />}
            <br />
            <TextArea style={{ marginTop: "6px", width: "98%" }}
                rows={5} className="inputField" type='text'
                label={'Comment'} size="small" variant='outlined'
                value={commentEntryState.commentSummary}
                onChange={(e) => {
                    setCommentEntryState(prevState => { return { ...prevState, "commentSummary": e.target.value } });
                }} />
            <b style={{ margin: "0px", padding: "0px" }}></b>
            <p style={{ margin: "0px", padding: "0px", paddingLeft: "20px" }}></p>
            <br />
        </div>;
        actions = [
            {
                key: 'submit', content: 'Submit', positive: true, onClick: async () => {
                    let resourceTitle = codeSystemTitle;
                    let response = await submitCodeSystemComment(apiToken, userId, userName, "comment", resourceId,
                        resourceTitle, conceptCode, conceptName,
                        conceptSelected?.definition, conceptSelected,
                        commentEntryState.commentTitle, commentEntryState.commentSummary,
                        commentEntryState.termVote, true, setCommentEntryState, null);

                    if (response?.success) {
                        navigate(`/resources/CodeSystem/${resourceId}#${conceptCode}`);
                    }
                }
            },
            { key: 'done', content: 'Cancel', negative: true }
        ];
    } else {
        loggedInContent = <div style={{ paddingLeft: "20px" }}>
            <h1>Please close window and login</h1>
        </div>;
        actions = [
            { key: 'done', content: 'Cancel', negative: true }
        ];
    }
    if (commentEntryState.modalExpanded === false) {
        actions = undefined;
    }
    return (
        <Modal
            trigger={<Button className="formButton subCommunicateButton" content="Comment"
                style={{ margin: "0px", color: "#000000" }}
                disabled={disableButton || viewingOlderVersion}
                onClick={() => {
                    if (userId) {
                        setCommentEntryState(prevState => {
                            return {
                                ...prevState,
                                "modalExpanded": true,
                                "commentTitle": `Comment on ${conceptName} by ${userName}`,
                                "commentSummary": ""
                            }
                        });
                    } else { alert("Please login to use the comment function."); }
                }} />}
            header={<div style={{ padding: "8px" }}><div style={{ paddingLeft: "10px", borderBottom: "1px solid" }}>
                <h2 style={{ paddingBottom: "6px" }}>{commentEntryState.commentTitle}</h2>
                <Button style={{ position: "absolute", padding: "0px 8px 0px 8px", fontWeight: "bold", right: "3px", top: "3px", backgroundColor: expandModalButtonBackgroundColor }}
                    className="formButton" content={commentEntryState.modalExpanded ? "▼" : "▶"}
                    onClick={() => {
                        setCommentEntryState(prevState => { return { ...prevState, "modalExpanded": !prevState.modalExpanded } });
                    }} />
            </div></div>}
            centered={false}
            closeOnDimmerClick={false}
            content={loggedInContent}
            actions={actions}
        />
    )
};

const TermVoteButton = ({ resourceId, codeSystemTitle,
    commentEntryState, setCommentEntryState }) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const navigate = useNavigate();
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const notificationEmail = useSelector(selectCurrentUserNotificationEmail);
    const conceptSelected = useSelector(selectConceptSelected);
    const conceptCode = useSelector(selectConceptCode);
    const conceptName = useSelector(selectConceptName);
    const conceptOpenForVoting = useSelector(selectConceptOpenForVoting);
    const votePermission = useSelector(selectVotePermission);

    let disableButton = false;
    if (conceptOpenForVoting === true) {
        disableButton = false;
    } else {
        disableButton = true;
    }
    let loggedInContent;
    let actions;
    let expandModalButtonBackgroundColor = "";
    let modalBodyContentStyle = { paddingLeft: "20px" };
    if (commentEntryState.modalExpanded === false) {
        expandModalButtonBackgroundColor = "#DAA520";
        modalBodyContentStyle["display"] = "none";
    }
    if (userId) {
        if (votePermission === true) {
            loggedInContent = <div>
                <div style={{ padding: "8px" }}>
                    <div style={{ paddingLeft: "10px", borderBottom: "1px solid" }}>
                        {(commentEntryState.termVote || commentEntryState.commentSummary) &&
                            <Beforeunload onBeforeunload={(event) => { event.preventDefault(); }} />}
                        <h2 style={{ paddingBottom: "6px" }}>
                            <span style={{ fontWeight: "normal" }}>Voting on </span>
                            {conceptName}
                            <span style={{ fontWeight: "normal" }}> as {userName}</span>
                        </h2>
                        <Button style={{
                            position: "absolute", padding: "0px 8px 0px 8px",
                            fontWeight: "bold", right: "3px", top: "3px", backgroundColor: expandModalButtonBackgroundColor
                        }}
                            className="formButton"
                            content={commentEntryState.modalExpanded ? "▼" : "▶"}
                            onClick={() => {
                                setCommentEntryState(prevState => {
                                    return { ...prevState, "modalExpanded": !prevState.modalExpanded }
                                });
                            }} />
                    </div>
                </div>
                <div style={modalBodyContentStyle}>
                    <br />
                    <fieldset style={{
                        width: "100%", display: "inline", border: "0px",
                        verticalAlign: "bottom", padding: "0px"
                    }}>
                        <input type="radio" name={"voteYesOrNo"} value="true"
                            checked={commentEntryState.termVote === "Yes"}
                            onChange={(e) => {
                                setCommentEntryState(prevState => { return { ...prevState, "termVote": "Yes" } });
                            }} />
                        <label style={{ fontSize: "16px" }}> Vote Yes on {conceptName} = {conceptSelected?.definition}</label>
                        <br /><br />
                        <input type="radio" name={"voteYesOrNo"} value="true"
                            checked={commentEntryState.termVote === "No"}
                            onChange={(e) => {
                                setCommentEntryState(prevState => { return { ...prevState, "termVote": "No" } });
                            }} />
                        <label style={{ fontSize: "16px" }}> Vote No on Term</label>
                    </fieldset>
                    <br /><br />
                    <span style={{ margin: "0px", padding: "0px" }}>Comment (required if you vote No):</span>
                    <p style={{ margin: "0px", padding: "0px", paddingLeft: "20px" }}>
                        <TextArea style={{ marginTop: "6px", width: "98%" }}
                            rows={5} className="inputField" type='text'
                            label={'Comment'} size="small" variant='outlined'
                            value={commentEntryState.commentSummary}
                            onChange={(e) => { setCommentEntryState(prevState => { return { ...prevState, "commentSummary": e.target.value } }); }} />
                    </p>
                    <br />
                </div>
            </div>;
        } else if (votePermission === false) {//TODO -- Should the phrase below be SEVCO or variable-based?
            loggedInContent = <div style={{ paddingLeft: "20px" }}>
                <br />
                <span><b>For voting privileges, please join the {resourceId === "27833" ? "GRADE Ontology Working Group" : "SEVCO Expert Working Group"}.</b></span>
                <br /><br />
                <span>Would you like to join the group in order to vote?</span>
                <br />
                <br />
            </div>;
        } else {
            loggedInContent = <div style={{ paddingLeft: "20px" }}>
                <br />
                <span>Loading...</span>
                <br />
                <br />
            </div>;
        }
        let disabledSubmit = false;
        if (commentEntryState.termVote === "" || (commentEntryState.termVote === "No" && commentEntryState.commentSummary === "")) {
            disabledSubmit = true;
        }
        if (votePermission === true) {
            actions = [
                {
                    key: 'submit', content: 'Submit', positive: true, disabled: disabledSubmit, onClick: async () => {
                        let resourceTitle = codeSystemTitle;
                        let response = await submitCodeSystemComment(apiToken, userId, userName, "vote",
                            resourceId, resourceTitle, conceptCode,
                            conceptName, conceptSelected?.definition,
                            conceptSelected, commentEntryState.commentTitle,
                            commentEntryState.commentSummary,
                            commentEntryState.termVote, true, setCommentEntryState, null);
                        if (response?.success) {
                            navigate(`/resources/CodeSystem/${resourceId}#${conceptCode}`);
                        }
                    }
                },
                { key: 'done', content: 'Cancel', negative: true }
            ];
        } else {
            actions = [
                {
                    key: 'join', content: 'Join Group', positive: true, onClick: async () => {
                        let response = await joinGroup(resourceId, apiToken);
                        if (!response || !response.success) {
                            if (response.error) {
                                alert(response.error);
                            } else {
                                alert("Joining the group was unsuccessful.");
                            }
                        }
                        if (response?.success) {
                            let emailMessage = "We don't have a notification email on file for you. Please visit your user profile by clicking your name in the upper right corner and add one. Otherwise, you won't receive a notification when terms are ready for vote.";
                            if (notificationEmail) {
                                emailMessage = `Your email on record is: ${notificationEmail} which we will use to send you notices when terms are ready for vote. If you want to change your email address used for notification, please edit your user profile by clicking your name in the upper right corner.`;
                            }
                            alert(`Welcome to the ${response.groupname}! ${emailMessage}`);
                            navigate(`/resources/CodeSystem/${resourceId}#${conceptCode}`);
                        }
                    }
                },
                { key: 'done', content: 'Cancel', negative: true }
            ];
        }
    } else {
        loggedInContent = <div style={{ paddingLeft: "20px" }}>
            <h1>Please close window and login before you vote.</h1>
        </div>;
        actions = [
            { key: 'done', content: 'Cancel', negative: true }
        ];
    }
    if (commentEntryState.modalExpanded === false) {
        actions = undefined;
    }
    return (
        <Modal
            trigger={
                <Button className="formButton subCommunicateButton" content="Vote"
                    style={{ margin: "0px", color: "#000000" }}
                    disabled={disableButton || viewingOlderVersion}
                    onClick={async () => {
                        if (userId) {
                            if (votePermission === undefined) {
                                let response = await votePermissionCheck(resourceId, apiToken);
                                if (response?.votepermission === true) {
                                    dispatch(changeVotePermission(true));
                                } else {
                                    dispatch(changeVotePermission(false));
                                }
                            }
                            setCommentEntryState(prevState => {
                                return {
                                    ...prevState,
                                    "modalExpanded": true,
                                    "commentTitle": `Voting on ${conceptName} as ${userName}`,
                                    "commentSummary": "",
                                    "termVote": ""
                                }
                            });
                        } else {
                            alert("Please login to vote.");
                        }
                    }
                    } />
            }
            centered={false}
            closeOnDimmerClick={false}
            content={loggedInContent}
            actions={actions}
        />
    )
};

const TermDetailLeftSideDisplay = ({ foi, termToLinkDict, setTermToLinkDict }) => {
    const { addToast } = useToasts();
    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceCollection = useSelector(state => selectAllResources(state));
    const changedResourcesList = useSelector(state => selectChangedResourcesList(state));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const apiToken = useSelector(selectCurrentUserApiToken);
    const connectionId = useSelector(selectSseConnectionId);
    const editPermission = parentResourceDetail?.permissionData?.editpermission;
    const tabFocus = useSelector(state => selectTabFocus(state));

    const [commentEntryState, setCommentEntryState] = useState({
        "modalExpanded": true, "commentTitle": "", "commentSummary": "", "termVote": ""
    });

    return <div className="left-sidebar-container-parent" style={{ float: "left", minWidth: "500px", marginRight: "18px" }}>
        <div className="left-sidebar-container" style={{ position: "fixed", width: "500px" }}>
            <h3 style={{ fontSize: "1.1rem", margin: "0px", textAlign: "center" }}>
                {resourceJson.title ?
                    <>{resourceJson.title}</>
                    :
                    <>{resourceJson.name ?
                        <>{resourceJson.name}</>
                        :
                        <><br /></>}</>}
            </h3>
            <NavigationCodeSystemTermSegment resourceId={foi} editMode={editMode} json={resourceJson}
                termToLinkDict={termToLinkDict} setTermToLinkDict={setTermToLinkDict} />
            <div>
                <div style={{ width: "100%", textAlign: "center", display: "flex" }}>
                    <TermCommentButton resourceId={foi} codeSystemTitle={resourceJson.title || resourceJson.name}
                        commentEntryState={commentEntryState} userId={userId}
                        setCommentEntryState={setCommentEntryState} />
                    <TermVoteButton resourceId={foi} codeSystemTitle={resourceJson.title || resourceJson.name}
                        commentEntryState={commentEntryState} userId={userId}
                        setCommentEntryState={setCommentEntryState} />
                    <Button style={{ marginLeft: "18px", marginRight: "0px", padding: "6px", width: "50%", color: "#000000", fontSize: "16px" }}
                        className="formButton navigationButton printButton"
                        content="🖨️ Print"
                        onClick={() => { window.print(); }}
                    />
                </div>
                <div style={{ marginTop: "10px" }} />
                <div style={{ width: "100%", display: "flex" }}>
                    <div style={{ width: "100%", float: "left", textAlign: "left" }}>
                        <Button className="formButton navigationButton"
                            style={{ color: "#000000", width: "100%" }} content={"Create Coding JSON/XML"} disabled />
                    </div>
                </div>
                <div style={{ marginTop: "10px" }} />
                <div style={{ width: "100%", textAlign: "center", display: "flex" }}>
                    <div style={{ width: "50%", float: "left", textAlign: "left" }}>
                        <Button className="formButton navigationButton"
                            style={{ color: "#000000", width: "97%", margin: "0px" }}
                            content={editMode ? "Update" : "Edit CodeSystem Term"}
                            onClick={editMode ? (() => {
                                dispatch(changeConceptDelete(false));
                                submitUpdatedResource(foi, parentResourceDetail, apiToken, connectionId, dispatch, resourceCollection,
                                    changedResourcesList, tabFocus, null, null, null, addToast);
                            }) : (() => {
                                dispatch(editModeOn());
                            })}
                            disabled={(!viewingOlderVersion && userName && editPermission) ? false : true} />
                    </div>
                    <div style={{ width: "50%", float: "right", textAlign: "right" }}>
                        <Button className="formButton navigationButton"
                            style={{ color: "#000000", width: "97%", margin: "0px" }}
                            content={"Clone CodeSystem Term"} disabled />
                    </div>
                </div>
            </div>
        </div>
    </div>
};

export default TermDetailLeftSideDisplay;